import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'i18n/TranslationProvider';
import DocumentList from 'components/documents/DocumentList';
import DocumentUploader from 'components/documents/DocumentUploader';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import {
  UnifiedFilterBar,
  UnifiedPageToolbar,
  UnifiedResultsSection,
  UnifiedStatCard,
  UnifiedSurfaceCard,
  useUnifiedButtonStyles,
} from 'components/layout/UnifiedPrimitives';
import API from 'api/api';

const PAGE_SIZE = 20;
const EMPTY_META = {
  page: 1,
  limit: PAGE_SIZE,
  total: 0,
  totalPages: 0,
};

const getListingLabel = (listing) =>
  listing?.humanId || listing?.title || listing?._id || '';

const getListingIdentity = (listing) => listing?._id || listing?.id || '';
const getQuoteListing = (quote) => {
  if (quote?.listing && typeof quote.listing === 'object') {
    return quote.listing;
  }
  if (quote?.listingId && typeof quote.listingId === 'object') {
    return quote.listingId;
  }
  return null;
};

const getListingRouteSnippet = (listing) => {
  const fromLabel = [listing?.fromCity, listing?.fromCountry].filter(Boolean).join(', ');
  const toLabel = [listing?.toCity, listing?.toCountry].filter(Boolean).join(', ');
  if (!fromLabel && !toLabel) return '';
  return [fromLabel || '—', toLabel || '—'].join(' -> ');
};

const formatStatusLabel = (status) => {
  const normalized = String(status || '').trim();
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const matchesListingSearch = (listing, searchValue) => {
  if (!searchValue) return true;
  const normalizedSearch = String(searchValue).toLowerCase();
  return [
    getListingLabel(listing),
    listing?.title,
    listing?.category,
    listing?.fromCity,
    listing?.toCity,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedSearch));
};

const fetchInvoiceCount = async (status) => {
  const params = { paginated: 'true', page: 1, limit: 1 };
  if (status && status !== 'all') {
    params.status = status;
  }
  const response = await API.get('/invoices', { params });
  return response?.data?.data?.meta?.total || 0;
};

const MyInvoices = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [deliveredListings, setDeliveredListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const cardBg = useColorModeValue('white', 'navy.800');
  const suggestionHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const { primaryButtonProps, secondaryButtonProps, ghostButtonProps } = useUnifiedButtonStyles();

  const uploadModal = useDisclosure();

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(EMPTY_META);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    paid: 0,
    unpaid: 0,
  });

  const [listingSearchInput, setListingSearchInput] = useState('');
  const [selectedUploadListing, setSelectedUploadListing] = useState(null);
  const [showListingSuggestions, setShowListingSuggestions] = useState(false);

  // Fetch partner's delivered/completed listings that are eligible for invoice upload.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setListingsLoading(true);
      try {
        const [quotesRes, invoicesRes] = await Promise.all([
          API.get('/quotes/my', { params: { status: 'accepted' } }),
          API.get('/invoices'),
        ]);
        if (!cancelled) {
          const quotes = Array.isArray(quotesRes?.data?.data)
            ? quotesRes.data.data
            : Array.isArray(quotesRes?.data)
              ? quotesRes.data
              : [];
          const existingInvoices = Array.isArray(invoicesRes?.data?.data)
            ? invoicesRes.data.data
            : Array.isArray(invoicesRes?.data)
              ? invoicesRes.data
              : [];
          const invoicedListingIds = new Set(
            existingInvoices
              .map((invoice) => invoice?.listingId || invoice?.bookingId)
              .filter(Boolean)
              .map(String),
          );

          const delivered = quotes
            .map((quote) => getQuoteListing(quote))
            .filter(Boolean)
            .filter((listing) => {
              const s = String(listing?.status || '').toLowerCase();
              return s === 'delivered' || s === 'completed';
            })
            .reduce((acc, listing) => {
              const listingId = getListingIdentity(listing);
              if (!listingId || invoicedListingIds.has(String(listingId))) {
                return acc;
              }
              if (!acc.some((item) => String(getListingIdentity(item)) === String(listingId))) {
                acc.push(listing);
              }
              return acc;
            }, []);
          setDeliveredListings(delivered);
        }
      } catch {
        // non-fatal — upload modal will show empty list
      } finally {
        if (!cancelled) setListingsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  const uploadListingSuggestions = useMemo(() => {
    return deliveredListings
      .filter((listing) => matchesListingSearch(listing, listingSearchInput))
      .slice(0, 10);
  }, [deliveredListings, listingSearchInput]);

  const handleMetaChange = useCallback((nextMeta) => {
    setMeta(nextMeta || EMPTY_META);
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(debounce);
  }, [searchInput]);

  useEffect(() => {
    if (meta?.totalPages > 0 && page > meta.totalPages) {
      setPage(meta.totalPages);
    }
  }, [meta?.totalPages, page]);

  useEffect(() => {
    let cancelled = false;
    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        const [total, paid, unpaid] = await Promise.all([
          fetchInvoiceCount('all'),
          fetchInvoiceCount('paid'),
          fetchInvoiceCount('unpaid'),
        ]);
        if (!cancelled) {
          setSummary({ total, paid, unpaid });
        }
      } catch (error) {
        if (!cancelled) {
          toast({
            title: t('Failed to load invoice summary'),
            status: 'error',
            duration: 3000,
          });
        }
      } finally {
        if (!cancelled) {
          setSummaryLoading(false);
        }
      }
    };

    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [refreshTrigger, t, toast]);

  const resetUploadState = () => {
    setListingSearchInput('');
    setSelectedUploadListing(null);
    setShowListingSuggestions(false);
  };

  const handleCloseUploadModal = () => {
    uploadModal.onClose();
    resetUploadState();
  };

  const totalPages = Math.max(meta?.totalPages || 0, 1);
  const canGoPrev = page > 1;
  const canGoNext = page < totalPages;

  return (
    <PageShell>
      <VStack spacing={6} align="stretch">
        <PageHeader
          title={t('My invoices')}
          description={t('Track paid and unpaid partner payout invoices, and upload the next delivered listing invoice quickly.')}
        />

        <UnifiedPageToolbar bg={cardBg}>
          <HStack justify="space-between" align={{ base: 'stretch', md: 'center' }} flexWrap="wrap" gap={3}>
            <Box>
              <Text fontWeight="700" fontSize="lg" mb={1}>
                {t('Partner Payout Invoices')}
              </Text>
              <Text fontSize="sm" color={mutedText}>
                {t('Track paid and unpaid invoices, expected payout dates, and upload new invoices quickly.')}
              </Text>
            </Box>
            <Button {...primaryButtonProps} onClick={uploadModal.onOpen}>
              {t('Upload Invoice')}
            </Button>
          </HStack>

          <HStack mt={4} spacing={3} flexWrap="wrap" align="stretch">
            <Box minW={{ base: '100%', md: '180px' }} flex="1">
              <UnifiedStatCard label={t('Total')} value={summaryLoading ? '...' : summary.total} />
            </Box>
            <Box minW={{ base: '100%', md: '180px' }} flex="1">
              <UnifiedStatCard label={t('Unpaid')} value={summaryLoading ? '...' : summary.unpaid} />
            </Box>
            <Box minW={{ base: '100%', md: '180px' }} flex="1">
              <UnifiedStatCard label={t('Paid')} value={summaryLoading ? '...' : summary.paid} />
            </Box>
          </HStack>
        </UnifiedPageToolbar>

        <UnifiedResultsSection
          bg={cardBg}
          title={t('Invoice documents')}
          meta={t('Filter invoice uploads, search by reference, and page through payout records.')}
        >
          <UnifiedFilterBar mb={4}>
            <Badge
              px={3}
              py={1.5}
              borderRadius="full"
              cursor="pointer"
              colorScheme={statusFilter === 'all' ? 'blue' : 'gray'}
              onClick={() => {
                setStatusFilter('all');
                setPage(1);
              }}
            >
              {t('All')}
            </Badge>
            <Badge
              px={3}
              py={1.5}
              borderRadius="full"
              cursor="pointer"
              colorScheme={statusFilter === 'unpaid' ? 'yellow' : 'gray'}
              onClick={() => {
                setStatusFilter('unpaid');
                setPage(1);
              }}
            >
              {t('Unpaid')}
            </Badge>
            <Badge
              px={3}
              py={1.5}
              borderRadius="full"
              cursor="pointer"
              colorScheme={statusFilter === 'paid' ? 'green' : 'gray'}
              onClick={() => {
                setStatusFilter('paid');
                setPage(1);
              }}
            >
              {t('Paid')}
            </Badge>
          </UnifiedFilterBar>

          <FormControl mb={4}>
            <FormLabel>{t('Search')}</FormLabel>
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t('Search by invoice number, file name, or listing ID')}
            />
          </FormControl>

          <DocumentList
            isInvoice
            canDelete={false}
            invoiceStatus={statusFilter}
            invoiceSearch={search}
            paginated
            page={page}
            limit={PAGE_SIZE}
            onMetaChange={handleMetaChange}
            refreshTrigger={refreshTrigger}
          />

          <HStack justify="space-between" mt={4} flexWrap="wrap" gap={2}>
            <Text fontSize="sm" color={mutedText}>
              {t('Showing page {{page}} of {{pages}} • {{total}} invoices', {
                page: meta?.page || page,
                pages: Math.max(meta?.totalPages || 0, 1),
                total: meta?.total || 0,
              })}
            </Text>
            <HStack spacing={2}>
              <Button {...secondaryButtonProps} size="sm" onClick={() => setPage((current) => current - 1)} isDisabled={!canGoPrev}>
                {t('Previous')}
              </Button>
              <Button {...secondaryButtonProps} size="sm" onClick={() => setPage((current) => current + 1)} isDisabled={!canGoNext}>
                {t('Next')}
              </Button>
            </HStack>
          </HStack>
        </UnifiedResultsSection>
      </VStack>

      <Modal isOpen={uploadModal.isOpen} onClose={handleCloseUploadModal} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Upload Invoice')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {listingsLoading ? (
              <HStack py={3}>
                <Spinner size="sm" />
                <Text fontSize="sm" color={mutedText}>{t('Loading delivered listings...')}</Text>
              </HStack>
            ) : (
              <VStack align="stretch" spacing={4}>
                <FormControl isRequired>
                  <FormLabel>{t('Listing')}</FormLabel>
                  <Box position="relative">
                    <Input
                      value={selectedUploadListing ? getListingLabel(selectedUploadListing) : listingSearchInput}
                      onChange={(event) => {
                        setSelectedUploadListing(null);
                        setListingSearchInput(event.target.value);
                        setShowListingSuggestions(true);
                      }}
                      onFocus={() => setShowListingSuggestions(true)}
                      onBlur={() => {
                        window.setTimeout(() => setShowListingSuggestions(false), 120);
                      }}
                      placeholder={t('Search delivered listing by reference, title, or route')}
                      pr="2.6rem"
                    />
                    <Box position="absolute" right="10px" top="10px" color={mutedText}>
                      <SearchIcon />
                    </Box>

                    {showListingSuggestions && !selectedUploadListing && (
                      <Box
                        position="absolute"
                        top="calc(100% + 6px)"
                        left={0}
                        right={0}
                        bg={cardBg}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="12px"
                        boxShadow="lg"
                        maxH="220px"
                        overflowY="auto"
                        zIndex={20}
                      >
                        {uploadListingSuggestions.length > 0 ? (
                          uploadListingSuggestions.map((listing) => (
                            <Box
                              key={getListingIdentity(listing)}
                              px={3}
                              py={2}
                              borderBottom="1px solid"
                              borderColor={borderColor}
                              cursor="pointer"
                              _hover={{ bg: suggestionHoverBg }}
                              onClick={() => {
                                setSelectedUploadListing(listing);
                                setListingSearchInput('');
                                setShowListingSuggestions(false);
                              }}
                            >
                              <Text fontSize="sm" fontWeight="600">
                                {getListingLabel(listing)}
                              </Text>
                              <Text fontSize="xs" color={mutedText} mt={0.5}>
                                {getListingRouteSnippet(listing) || t('Route details available in listing')}
                              </Text>
                            </Box>
                          ))
                        ) : (
                          <Box px={3} py={2}>
                            <Text fontSize="sm" color={mutedText}>
                              {t('No matching delivered listings')}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </FormControl>

                {selectedUploadListing && (
                  <UnifiedSurfaceCard p={4}>
                    <VStack align="stretch" spacing={1.5}>
                      <HStack justify="space-between" align="flex-start">
                        <Box>
                          <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color={mutedText}>
                            {t('Selected listing')}
                          </Text>
                          <Text fontWeight="700">{getListingLabel(selectedUploadListing)}</Text>
                          {selectedUploadListing?.title && (
                            <Text fontSize="sm" color={mutedText}>
                              {selectedUploadListing.title}
                            </Text>
                          )}
                        </Box>
                        <Badge colorScheme="green" borderRadius="full" px={2.5}>
                          {formatStatusLabel(selectedUploadListing?.status || 'delivered')}
                        </Badge>
                      </HStack>
                      <Text fontSize="sm" color={mutedText}>
                        {getListingRouteSnippet(selectedUploadListing)}
                      </Text>
                    </VStack>
                  </UnifiedSurfaceCard>
                )}

                <DocumentUploader
                  isInvoice
                  entityType="listing"
                  entityId={getListingIdentity(selectedUploadListing) || undefined}
                  docType="invoice"
                  onUploaded={() => {
                    setRefreshTrigger((current) => current + 1);
                    toast({ title: t('Invoice uploaded'), status: 'success', duration: 2500 });
                    handleCloseUploadModal();
                  }}
                />
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button {...ghostButtonProps} onClick={handleCloseUploadModal}>
              {t('Close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
};

export default MyInvoices;
